export interface EdgeRouterMatch {
  steps: any[];
  index: number;
  step?: any;
  route?: any;
}

/** Resolve edge targets once per relevant step-tree change, keeping live route
 * references so labels and deletion controls still observe in-place edits. */
export function createEdgeRouterLookup(
  steps: any[]
): ReadonlyMap<unknown, EdgeRouterMatch | ""> {
  const parentByOrigId = new Map<unknown, unknown>();
  const collectTargets = (list: any[]) => {
    list.forEach((step) => {
      // findStepByOrigId returns the first depth-first match, including routers.
      if (!parentByOrigId.has(step.origId)) {
        parentByOrigId.set(step.origId, step.parentId);
      }
      if (
        step.type &&
        step.type !== "LOOP" &&
        step.type !== "LOOP_END" &&
        step.routes
      ) {
        step.routes.forEach((route) => {
          if (Array.isArray(route.steps)) collectTargets(route.steps);
        });
      }
    });
  };

  const parentById = new Map<unknown, EdgeRouterMatch>();
  const collectParents = (list: any[]) => {
    list.forEach((step, index) => {
      if (step.routes) {
        step.routes.forEach((route, routeIndex) => {
          if (Array.isArray(route.steps)) collectParents(route.steps);
          if (route.id) {
            parentById.set(route.id, {
              steps: step.routes,
              index: routeIndex,
              route,
            });
          }
        });
      }
      // findRouteById keeps the last sibling match but stops descending below
      // a matching step/route. Post-order writes preserve both precedence rules.
      if (step.id) parentById.set(step.id, { steps: list, index, step });
    });
  };

  collectTargets(steps);
  collectParents(steps);
  const lookup = new Map<unknown, EdgeRouterMatch | "">();
  parentByOrigId.forEach((parentId, origId) => {
    lookup.set(origId, parentById.get(parentId) ?? "");
  });
  return lookup;
}
